import { useEffect, useMemo, useReducer } from 'react';
import { useNotifications } from '../../../contexts/notificationContext';
import { Result, StringWithFormat } from '../../../types/Result';
import { ControlProps, FieldConfig, FieldProps, FormConfig, FormState, FormStateChangeAction, LabelProps } from '../../Fields/Types';
import getValidator from './validators';

const createValidationReducer = () => (state: FormState, action: FormStateChangeAction): FormState => {

    switch (action.type) {

        case 'change-field':
            const changedFields = { ...state.changedFields, [action.payload]: true };
            return {
                ...state,
                dirty: true,
                changedFields
            };

        case 'change-field-after-submit':
            const changedFieldsAfterSubmit = { ...state.changedFieldsAfterSubmit, [action.payload]: true };
            return { ...state, changedFieldsAfterSubmit };

        case 'clear-change-field-after-submit':
            return { ...state, changedFieldsAfterSubmit: {} };

        case 'submit':
            return { ...state, submitted: true };

        case 'blur':
            const blurredFields = { ...state.blurredFields, [action.payload]: true };
            return { ...state, blurredFields };

        default:
            throw new Error('Unknown action type');
    }
};

function getInitialState(formConfig: FormConfig): FormState {
    const initialBlurred: Record<string, boolean> = {};

    for (let fieldName in formConfig.fields) {
        initialBlurred[fieldName] = false;
    }

    return {
        submitted: false,
        dirty: false,
        blurredFields: initialBlurred,
        changedFields: {},
        changedFieldsAfterSubmit: {},
    };
}

function validateForm(formData: any | null, fields: FieldConfig[]): Record<string, StringWithFormat[]> {
    const validationResult: Record<string, StringWithFormat[]> = {};

    if (formData == null)
        return validationResult;

    for (let i = 0; i < fields.length; i++) {
        const field = fields[i];
        const fieldValue = getFieldValue(formData, field.name);

        const error = validateField(fieldValue, field);
        if (error.length > 0)
            validationResult[field.name] = error;
    };

    return validationResult;
}

function validateField(fieldValue: any, fieldConfig: FieldConfig): StringWithFormat[] {

    let result: StringWithFormat[] = [];

    if (fieldConfig.validationRules) {
        for (let i = 0; i < fieldConfig.validationRules.length; i++) {
            const validatorConfig = fieldConfig.validationRules[i];
            const configuredValidator = getValidator(fieldConfig.label, validatorConfig);
            if (configuredValidator) {
                const validationResult = configuredValidator.validate(fieldValue);
                if (validationResult) {
                    result.push(validationResult);
                }
            }
        }
    }

    return result;
}

function getErrors(errors: Record<string, StringWithFormat[]>, serverErrors: Result | undefined, state: FormState, formConfig: FormConfig) {

    if (state.submitted !== true)
        return {};

    if (serverErrors) {
        if (serverErrors.isSuccesful === false) {
            serverErrors.messages.forEach(function (message) {
                if (message.memberNames && message.memberNames.length > 0) {
                    let fieldNames: string[] = []
                    let changed = false
                    for (var i = 0; i < message.memberNames.length; i++) {
                        fieldNames.push(message.memberNames[i].charAt(0).toLowerCase() + message.memberNames[i].slice(1));
                        if (state.changedFieldsAfterSubmit[fieldNames[i]] !== undefined)
                            changed = true
                    }

                    if (changed === false) {
                        for (i = 0; i < fieldNames.length; i++) {
                            if (errors[fieldNames[i]] === undefined) {
                                errors[fieldNames[i]] = []
                            }

                            errors[fieldNames[i]].push(message.message)
                            console.error(message.message)
                        }
                    }
                }
            })
        }
    }

    if (formConfig.showErrors === 'always') {
        return errors
    }
    else {
        const blurredErrors: Record<string, StringWithFormat[]> = {}

        for (let key in errors) {
            if (state.blurredFields[key])
                blurredErrors[key] = errors[key]
        }

        return blurredErrors
    }
}

function getFieldConfig(formConfig: FormConfig, fieldName: string): FieldConfig | undefined {
    let result: FieldConfig | undefined;

    formConfig.fields.forEach(function (fieldConfig) {
        if (fieldConfig.name === fieldName)
            result = fieldConfig;
    });

    if (!result) {
        console.log("field with name '" + fieldName + "' not found.");
        console.log('formConfig', formConfig);
    }

    return result;
}

function getIsRequired(fieldConfig: FieldConfig): boolean {
    var isRequired: boolean = false;
    if (fieldConfig) {
        if (fieldConfig.validationRules) {
            fieldConfig.validationRules.forEach(function (rule) {
                if (rule.type === 'isRequired' || rule.type === 'isRequiredNumber')
                    isRequired = true;

            });
        }
    }

    return isRequired;
}

function getFieldValue(formData: any, fieldName: string): any {
    if (formData)
        return fieldName.split('.').reduce((object, propertyName) => {
            return (object && object[propertyName] !== undefined) ? object[propertyName] : null;
        }, formData);
}

function setFieldValue<Data>(formData: Data, name: string, value: string) {
    if (formData) {
        name.split('.').reduce((object, propertyName, index, elements) => {
            if (index === elements.length - 1)
                object[propertyName] = value;

            if (object && object[propertyName])
                return object[propertyName];

            return null;
        }, formData);
    }
}

export default function useValidation<Data>(formConfig: FormConfig, formData: Data | undefined, serverErrors?: Result | undefined, submitCount?: number) {
    const validationReducer = createValidationReducer();
    const initialState = getInitialState(formConfig);
    const [state, dispatch] = useReducer(validationReducer, initialState);
    const { notify } = useNotifications()

    const validationResult = validateForm(formData, formConfig.fields);
    const validationResults = useMemo(() => getErrors(validationResult, serverErrors, state, formConfig), [validationResult, serverErrors, state.submitted, formConfig.showErrors]);

    useEffect(() => {
        if (submitCount) {
            dispatch({ type: 'submit' });

            if (Object.keys(validationResult).length === 0) {
                if (formConfig.handleSubmit) {
                    formConfig.handleSubmit();
                    dispatch({ type: 'clear-change-field-after-submit' });
                }
            }
            else {
                notify("warning", "Data is not saved!")
            }
        }
    }, [submitCount])

    return {
        getSubmitButtonProps: function () {
            return {
                disabled: !formData || (state.submitted && Object.keys(validationResult).length > 0),

                onClick: (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
                    event.preventDefault();

                    dispatch({ type: 'submit' });

                    if (Object.keys(validationResult).length === 0) {
                        if (formConfig.handleSubmit) {
                            formConfig.handleSubmit();
                            dispatch({ type: 'clear-change-field-after-submit' });
                        }
                    }
                    else {
                        notify("warning", "Data is not saved!")
                    }
                }
            };
        },

        getLabelProps: function (fieldName: string): LabelProps {
            var fieldConfig = getFieldConfig(formConfig, fieldName)!;

            let labelProps: LabelProps = {
                text: '[' + fieldName + '] not found',
                labelSize: 2,
                required: false,
            };

            if (fieldConfig) {
                labelProps.text = fieldConfig.label;
                labelProps.required = getIsRequired(fieldConfig);

                if (fieldConfig.labelSize)
                    labelProps.labelSize = fieldConfig.labelSize;
            }

            return labelProps;
        },

        getControlProps: function (fieldName: string): ControlProps {
            let controlProps: ControlProps = {
                name: fieldName,
                value: getFieldValue(formData, fieldName),
                validationResults: validationResults[fieldName],

                onChange: function (value: string) {
                    if (formData) {
                        setFieldValue(formData, fieldName, value/*, setFormData*/)

                        if (formConfig.handleFieldChanged)
                            formConfig.handleFieldChanged(fieldName, value);

                        dispatch({ type: 'change-field', payload: fieldName });

                        if (state.submitted)
                            dispatch({ type: 'change-field-after-submit', payload: fieldName });
                    }
                },

                onKeyUp: (key: any) => {
                    if (formConfig.handleKeyUp)
                        formConfig.handleKeyUp(fieldName, key);
                },


                onBlur: () => {
                    dispatch({ type: 'blur', payload: fieldName });
                }
            };
            return controlProps;
        },

        getFieldProps: function (fieldName: string): FieldProps {
            var fieldConfig = getFieldConfig(formConfig, fieldName)!;

            let fieldProps: FieldProps = {
                labelProps: {
                    text: fieldConfig.label,
                    labelSize: fieldConfig.labelSize ? fieldConfig.labelSize : 2,
                    required: getIsRequired(fieldConfig),
                },
                controlProps: {
                    name: fieldName,
                    value: getFieldValue(formData, fieldName),
                    validationResults: validationResults[fieldName],

                    onChange: function (value: string) {
                        if (formData) {
                            setFieldValue(formData, fieldName, value/*, setFormData*/)

                            if (formConfig.handleFieldChanged)
                                formConfig.handleFieldChanged(fieldName, value);

                            dispatch({ type: 'change-field', payload: fieldName });

                            if (state.submitted)
                                dispatch({ type: 'change-field-after-submit', payload: fieldName });
                        }
                    },

                    onKeyUp: (key: any) => {
                        if (formConfig.handleKeyUp)
                            formConfig.handleKeyUp(fieldName, key);
                    },

                    onBlur: () => {
                        dispatch({ type: 'blur', payload: fieldName });
                    }
                }
            };

            return fieldProps;
        }
    };
};